import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BookCover from './BookCover';

const ShelfRow = ({ icons, index = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative mb-10 md:mb-12 select-none">
      {/* Books row */}
      <div className="relative z-10 flex items-end justify-center gap-4 sm:gap-7 md:gap-8 px-2 sm:px-4 mb-1">
        {icons.map((book, i) => (
          <div
            key={book.id}
            className="shrink-0 w-[88px] sm:w-20 md:w-[100px] lg:w-[120px]"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: `opacity 0.5s ease ${i * 0.06}s, transform 0.5s ease ${i * 0.06}s`,
            }}
          >
            <a
              href={`/libro/${book.slug}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/libro/${book.slug}`);
              }}
              draggable={false}
              className="block select-none transition-transform duration-200 ease-out hover:scale-[1.08] hover:-translate-y-1"
            >
              <div className="relative w-full overflow-hidden rounded-lg cursor-pointer" style={{ aspectRatio: '3/4' }}>
                <BookCover book={book} />
              </div>
          </a>
          </div>
        ))}
      </div>

      {/* 3D Shelf bar */}
      <div
        className="relative mt-[-12px]"
        style={{ perspective: '800px' }}
      >
        <div
          className="relative h-5 rounded-sm"
          style={{
            transformOrigin: 'bottom center',
            transform: 'rotateX(20deg)',
            background: 'linear-gradient(180deg, rgba(220,225,230,0.4) 0%, rgba(230,233,237,0.7) 50%, rgba(240,242,244,0.85) 100%)',
            boxShadow: '0 2px 1px rgba(89,108,124,0.2), 0 4px 0 rgba(160,175,188,0.3), 0 12px 16px -6px rgba(0,0,0,0.1)',
            opacity: isVisible ? 1 : 0,
            transition: `opacity 0.6s ease ${icons.length * 0.06}s`,
          }}
        >
          {/* Reflections */}
          <div
            className="absolute left-0 right-0 flex items-start justify-center gap-4 sm:gap-7 md:gap-8 px-2 sm:px-4 overflow-hidden"
            style={{ top: '12px', bottom: 0 }}
          >
            {icons.map((book) => (
              <div
                key={`ref-${book.id}`}
                className="shrink-0 w-[88px] sm:w-20 md:w-[100px] lg:w-[120px] h-full"
              >
                <div
                  className="w-full h-full"
                  style={{
                    transform: 'scaleY(-1) rotateX(-8deg)',
                    transformOrigin: 'center center',
                    borderRadius: '0 0 35% 35%',
                    opacity: 0.25,
                    filter: 'blur(1.5px)',
                    backgroundImage: book.coverUrl ? `url(${book.coverUrl})` : `linear-gradient(145deg, ${book.coverColors?.[0] || '#6366F1'}, ${book.coverColors?.[1] || '#4F46E5'})`,
                    backgroundSize: '100% 125%',
                    backgroundPosition: 'bottom',
                    backgroundRepeat: 'no-repeat',
                  }}
                  aria-hidden="true"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Light edge line */}
        <div
          className="absolute left-[1%] right-[1%] top-full h-px"
          style={{
            background: 'linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 35%, rgba(255,255,255,0.8) 65%, rgba(255,255,255,0.5) 100%)',
          }}
        />
      </div>
    </div>
  );
};

export default ShelfRow;
